import React from "react"
import Content from "@love-irish/core-content"

import CodexSenses, { CodexSectionView } from "./senses"
import { addDynamicTags } from "@love-irish/core-text"
import { CodexPronunciation } from "./codex_pronunciation"
import { labels } from "@love-irish/i18n"
import { CodexFieldLabel } from "@love-irish/resource-codex-meta-components"

export interface CodexMetaProps {
  item: {
    name?: string
    translation?: string | null
    summary?: string | null
    content?: string | null
    pronunciation?: string | null
    ipaPronunciation?: string | null
    sections?: CodexSectionView[] | null
    [key: string]: any
  }
  /** Show the conjugation chart (the show page). Off for condensed list/card views. */
  showConjugation?: boolean
  /** Render the pronunciation/IPA line. Off when the caller already shows it (e.g. inline by the
   *  headword in the dictionary list), so it isn't duplicated. Default on. */
  showPronunciation?: boolean
}

/**
 * Renders a codex record's meaning: the POS-grouped sense sections when the record is enriched with
 * senses, otherwise the base translation + summary. One branch, shared across the show page, the
 * dictionary list, and the generic list item.
 */
export const CodexMeta: React.FC<CodexMetaProps> = ({
  item,
  showConjugation = false,
  showPronunciation = true,
}) => {
  const sections = item?.sections ?? []

  if (sections.length > 0) {
    return (
      <>
        {/* Pronunciation renders for enriched (sensed) records too, not just base ones — it's the same
            /pronunciation/ [?]  /IPA/ [?] line the show page + base cards show. */}
        {showPronunciation && (item.pronunciation || item.ipaPronunciation) && (
          <div className="mt-1">
            <CodexPronunciation
              pronunciation={item.pronunciation}
              ipaPronunciation={item.ipaPronunciation}
            />
          </div>
        )}
        <CodexSenses
          sections={sections}
          headword={item.name}
          showConjugation={showConjugation}
          item={item}
        />
      </>
    )
  }

  // Not yet enriched with senses: show the base translation and summary.
  return (
    <div className="mt-1 text-sm text-muted-foreground space-y-3">
      {/* Condensed one line: /pronunciation/ [?]  /IPA/ [?] — each "?" opens its cheat-sheet. */}
      {showPronunciation && (
        <CodexPronunciation
          pronunciation={item.pronunciation}
          ipaPronunciation={item.ipaPronunciation}
        />
      )}

      {item.translation && (
        <div className="pt-2">
          <CodexFieldLabel>
            {labels.translation.text} / {labels.translation.translation}
          </CodexFieldLabel>
          <Content
            content={addDynamicTags(item.translation)}
            className="prose-sm text-sm text-dark dark:text-white font-mono! first-line:not-italic"
          />
        </div>
      )}

      {(item?.summary || item?.content) && (
        <Content content={item.summary || item.content} className=" text-sm" />
      )}
    </div>
  )
}

export default CodexMeta
